import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Radio } from '@ui/components/Radio'
import { InfoIcon } from '@ui/icons/Info.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useNotificationUnread } from '@utils/notifications'

import { NotificationPreferences } from '@model/Notification'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { InformationModal } from '@shared/modals/Information.modal'

import { initialNotificationPreferences, notificationOptions } from './SettingConsts'

import styles from './AccountSettingsPage.module.scss'

export const NotificationPreferencesBlock: React.FC = () => {
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { account } = useAccountReadContext()
  const [loading, setLoading] = useState(false)
  const { refreshAllNotifications } = useNotificationUnread()

  const [preferences, setPreferences] = useState<NotificationPreferences>({
    ...initialNotificationPreferences,
    clientId: account?.id,
  })

  const { data: notificationPreferencesFetch } = useQuery({
    queryKey: queryKeys.profile.notificationPreferences(),
    queryFn: () => apiClient.getNotificationPreferences(),
  })

  useEffect(() => {
    if (notificationPreferencesFetch) {
      setPreferences(notificationPreferencesFetch)
    }
  }, [notificationPreferencesFetch])

  const updateNotificationPreferences = async (
    id: keyof NotificationPreferences,
    value: boolean
  ) => {
    try {
      setLoading(true)
      if (!preferences) {
        return
      }
      if (preferences[id] === value) {
        return
      }
      const newPreferences = {
        ...preferences,
        [id]: value,
      }
      setPreferences((prev) => ({
        ...prev,
        [id]: value,
      }))
      await apiClient.updateNotificationPreferences(newPreferences)
      await queryClient.invalidateQueries({ queryKey: ['account'] })
      refreshAllNotifications()
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  const infoModal = useModal(InformationModal, { secondaryLabel: t('Got It') })

  return (
    <div className={styles.notifications}>
      <Loading asOverlay showLoadingIcon isLoading={loading}>
        <table>
          <thead className={styles.header}>
            <tr>
              <th />
              <th>
                <Body2 isParagraph className={styles.label}>
                  {t('Notifications.Settings.Silent')}
                  <Box ml='x4' maxHeight='20px'>
                    <ActionIcon
                      icon={InfoIcon}
                      size='x20'
                      onClick={() =>
                        infoModal.open({
                          title: t('Notifications.Settings.Silent Notification'),
                          description: t(
                            'Notifications.Settings.Silent notification does not interrupt you'
                          ),
                        })
                      }
                      data-test='account-settings-notification-silent-info'
                    />
                  </Box>
                </Body2>
              </th>
              <th>
                <Body2 className={styles.label}>
                  {t('Notifications.Settings.Regular')}
                  <Box ml='x4' maxHeight='20px'>
                    <ActionIcon
                      icon={InfoIcon}
                      size='x20'
                      onClick={() =>
                        infoModal.open({
                          title: t('Notifications.Settings.Regular Notification'),
                          description: t('Notifications.Settings.Regular notifications are'),
                        })
                      }
                      data-test='account-settings-notification-silent-info'
                    />
                  </Box>
                </Body2>
              </th>
            </tr>
          </thead>
          <tbody>
            {notificationOptions(t, preferences).map((notificationOption) => (
              <tr className={styles.notificationItem}>
                <td className={styles.name}>
                  {notificationOption.name}
                  <br />
                  <Body2 className={styles.info}>{notificationOption.label}</Body2>
                </td>
                <td className={styles.options}>
                  <Box mt='x16' flex justify='center'>
                    <Radio
                      name={notificationOption.id}
                      checked={!!notificationOption.value}
                      data-test={`account-settings-notification-${notificationOption.id}-silent`}
                      onChange={() => updateNotificationPreferences(notificationOption.id, true)}
                    />
                  </Box>
                </td>
                <td className={styles.options}>
                  <Box mt='x16' flex justify='center'>
                    <Radio
                      checked={!notificationOption.value}
                      data-test={`account-settings-notification-${notificationOption.id}-regular`}
                      onChange={() => updateNotificationPreferences(notificationOption.id, false)}
                      name={notificationOption.id}
                    />
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Loading>
    </div>
  )
}
